import axios from 'axios'
import type { AxiosInstance, AxiosResponse, ResponseType } from 'axios'
import { useSessionStore } from '@/stores/session'
import router from '@/router'
import {
  ENROLLMENT_DEVICE_PROOF_HEADER,
  enrollmentDeviceProofFor,
  enrollmentInvitationTokenFromUrl
} from '@/modules/enrollmentDeviceProof'
export class Request {
  private readonly axiosInstance: AxiosInstance
  protected baseUrl: undefined | string
  // Some endpoints answer 403 for a business reason (a team's schedule is
  // not shared with families) rather than a dead session; those callers opt
  // out of the lobby redirect and surface the server's failure_reason.
  private readonly redirectOnForbidden: boolean

  constructor(
    baseUrl?: string,
    responseType = 'json',
    options: { redirectOnForbidden?: boolean } = {}
  ) {
    this.redirectOnForbidden = options.redirectOnForbidden ?? true
    this.axiosInstance = axios.create({
      xsrfCookieName: `${import.meta.env.VITE_COOKIE_PREFIX.toUpperCase()}-USER-SERVICES-XSRF-TOKEN`,
      xsrfHeaderName: `X-${import.meta.env.VITE_COOKIE_PREFIX.toUpperCase()}-USER-SERVICES-XSRF-TOKEN`
    })
    if (baseUrl === undefined) {
      baseUrl = import.meta.env.VITE_BACKEND_URL
    }
    this.baseUrl = baseUrl
    this.axiosInstance.defaults.withCredentials = true
    this.axiosInstance.defaults.withXSRFToken = true
    this.axiosInstance.defaults.responseType = responseType as ResponseType
    this.axiosInstance.interceptors.request.use(config => {
      const token = localStorage.getItem('auth_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      // SOW-300: the public enrollment invitation endpoints (saved-card payment,
      // dashboard-token) want this device's proof that the family entered the code.
      const invitationToken = enrollmentInvitationTokenFromUrl(config.url ?? '')
      const proof = invitationToken ? enrollmentDeviceProofFor(invitationToken) : null
      if (proof) {
        config.headers[ENROLLMENT_DEVICE_PROOF_HEADER] = proof
      }
      return config
    })
    this.enableUnauthorizedRedirect()
  }

  enableUnauthorizedRedirect() {
    const onResponse = (response: AxiosResponse): AxiosResponse => {
      // Bump timeout if we're past the auth middleware
      if (response.status !== 401) {
        if (typeof response.data === 'object' && response.data.session_idle_at === undefined) {
          useSessionStore().madeRequest()
        }
      }

      return response
    }

    const onErrorResponse = (error: any): Promise<any> => {
      if (axios.isAxiosError(error)) {
        const { status } = error.response ?? {}
        const url = error.config?.url ?? ''

        // Don't redirect on auth errors during logout — the 401 is expected
        // when there's no Sanctum token (e.g. lobby-only login)
        const isLogoutRequest = url.includes('/logout')

        const isToleratedForbidden = status === 403 && !this.redirectOnForbidden

        if ((status === 401 || status === 403) && !isLogoutRequest && !isToleratedForbidden) {
          console.error('Authentication error:', status)
          this.handleAuthenticationError(status)
        }
      }

      return Promise.reject(error)
    }

    this.axiosInstance.interceptors.response.use(onResponse, onErrorResponse)
  }

  private handleAuthenticationError(status: number) {
    const LOBBY_URL = import.meta.env.VITE_LOBBY_URL
    const currentPath = window.location.pathname

    if (currentPath.startsWith('/standalone/')) {
      return
    }

    const isInIframe = this.detectIframeContext()

    if (isInIframe || currentPath.includes('powerschool')) {
      // PowerSchool authentication context - redirect to landing page for re-authentication
      console.log(
        'PowerSchool authentication error, redirecting to PowerSchool landing page for re-auth'
      )
      // In iframe context, redirect to PowerSchool landing page to trigger SSO re-authentication
      router.push({ name: 'LandingPowerschool' })
    } else {
      // Lobby SSO or regular authentication context
      console.log('Lobby authentication error, redirecting to lobby')
      window.location.href = LOBBY_URL
    }
  }

  private detectIframeContext(): boolean {
    try {
      return window.self !== window.top || window.frameElement !== null
    } catch (e) {
      // Frame access blocked, assume iframe context
      return true
    }
  }

  // noinspection JSUnusedGlobalSymbols
  disableUnauthorizedRedirect() {
    this.axiosInstance.interceptors.response.use((response: any): any => {
      if (response.data.status === 401) {
        router.push('/login')
      }
    })
  }

  uriToUrl(uri: string, additionalParams?: object) {
    let url = new URL(this.baseUrl + uri)
    if (import.meta.env.VITE_XDEBUG_SESSION_VALUE !== undefined) {
      url.searchParams.append('XDEBUG_SESSION', import.meta.env.VITE_XDEBUG_SESSION_VALUE)
    }
    if (additionalParams !== undefined) {
      Object.keys(additionalParams).forEach((key: string) => {
        url.searchParams.append(key, additionalParams[key as keyof object])
      })
    }
    return url.toString()
  }

  get(uri: string, additionalParams?: { signal?: AbortSignal } & Record<string, any>) {
    return this.axiosInstance.get(this.uriToUrl(uri, additionalParams), {
      signal: additionalParams?.signal
    })
  }

  post(uri: string, payload?: any, additionalParams?: object) {
    return this.axiosInstance.post(this.uriToUrl(uri, additionalParams), payload)
  }

  delete(uri: string, payload?: any, additionalParams?: object) {
    return this.axiosInstance.delete(this.uriToUrl(uri, additionalParams), payload)
  }
}
